/**
 * Scope Type Modal - Add/Edit organization types
 * Used in Settings page for managing organization types (scope types)
 */
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useCreateScopeType, useUpdateScopeType } from '@/hooks/useScopeTypes';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import type { ScopeType } from '@/lib/schemas';

// Form validation schema
const scopeTypeFormSchema = z.object({
  display_name: z.string().min(1, 'Display name is required').max(200, 'Display name too long'),
  name: z.string().min(1, 'Slug is required').max(100, 'Slug too long'),
  is_client_type: z.boolean().default(false),
  display_order: z.number().int().min(0, 'Display order must be 0 or greater'),
});

type ScopeTypeFormData = z.infer<typeof scopeTypeFormSchema>;

interface ScopeTypeModalProps {
  isOpen: boolean;
  onClose: () => void;
  scopeType: ScopeType | null; // null = create mode, ScopeType = edit mode
  existingScopeTypes: ScopeType[];
}

/**
 * Modal for creating or editing a scope type.
 * Used in Settings → Organization Types tab.
 */
export function ScopeTypeModal({
  isOpen,
  onClose,
  scopeType,
  existingScopeTypes,
}: ScopeTypeModalProps) {
  const isEditMode = !!scopeType;
  const createScopeType = useCreateScopeType();
  const updateScopeType = useUpdateScopeType();

  // Track if slug was manually edited (to stop auto-generation)
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm<ScopeTypeFormData>({
    resolver: zodResolver(scopeTypeFormSchema),
    defaultValues: {
      display_name: '',
      name: '',
      is_client_type: false,
      display_order: 0,
    },
  });

  const isClientType = watch('is_client_type');
  const displayName = watch('display_name');
  const name = watch('name');

  // Initialize form when opening in edit mode
  useEffect(() => {
    if (isOpen && scopeType) {
      setValue('display_name', scopeType.display_name);
      setValue('name', scopeType.name);
      setValue('is_client_type', scopeType.is_client_type);
      setValue('display_order', scopeType.display_order);
      setSlugManuallyEdited(false); // Reset on open
    } else if (isOpen && !scopeType) {
      // Create mode - set default display_order to max + 1
      const maxOrder = existingScopeTypes.reduce(
        (max, type) => Math.max(max, type.display_order),
        0
      );
      setValue('display_order', maxOrder + 1);
      setSlugManuallyEdited(false);
    }
  }, [isOpen, scopeType, existingScopeTypes, setValue]);

  // Auto-generate slug from display name (unless manually edited)
  useEffect(() => {
    if (isOpen && !isEditMode && !slugManuallyEdited && displayName) {
      const autoSlug = slugify(displayName);
      setValue('name', autoSlug);
    }
  }, [displayName, isOpen, isEditMode, slugManuallyEdited, setValue]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      reset();
      setSlugManuallyEdited(false);
    }
  }, [isOpen, reset]);

  /**
   * Convert display name to slug format
   * Example: "Structural Engineer" → "structural_engineer"
   */
  const slugify = (text: string): string => {
    return text
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '') // Remove special chars
      .replace(/[\s_-]+/g, '_') // Replace spaces/dashes with underscores
      .replace(/^_+|_+$/g, ''); // Remove leading/trailing underscores
  };

  /**
   * Validate unique name (slug)
   * Check against existing types, excluding current type in edit mode
   */
  const validateUniqueName = (formData: ScopeTypeFormData): boolean => {
    const duplicate = existingScopeTypes.find(
      (type) =>
        type.name.toLowerCase() === formData.name.toLowerCase() &&
        type.id !== scopeType?.id // Exclude self in edit mode
    );

    if (duplicate) {
      // Show error (could enhance with toast)
      alert(`A client type with slug "${formData.name}" already exists.`);
      return false;
    }

    return true;
  };

  const onSubmit = async (formData: ScopeTypeFormData) => {
    // Validate unique name
    if (!validateUniqueName(formData)) {
      return;
    }

    try {
      if (isEditMode) {
        // Update existing scope type
        await updateScopeType.mutateAsync({
          id: scopeType.id,
          data: {
            display_name: formData.display_name,
            name: formData.name,
            is_client_type: formData.is_client_type,
            display_order: formData.display_order,
          },
        });
      } else {
        // Create new scope type
        await createScopeType.mutateAsync({
          display_name: formData.display_name,
          name: formData.name,
          is_client_type: formData.is_client_type,
          display_order: formData.display_order,
        });
      }

      // Close modal
      onClose();

      // Reset form
      reset();
    } catch (error) {
      // Error is already handled by the mutation hooks (toast shown)
      console.error('Failed to save scope type:', error);
    }
  };

  const handleCancel = () => {
    reset();
    setSlugManuallyEdited(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle>{isEditMode ? 'Edit' : 'Create'} Client Type</DialogTitle>
          <DialogDescription>
            {isEditMode
              ? 'Update the client type details.'
              : 'Create a new client type for categorizing clients and project billing tracks.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Display Name */}
          <div>
            <Label htmlFor="display_name">
              Display Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="display_name"
              placeholder="e.g., Structural Engineer"
              {...register('display_name')}
            />
            {errors.display_name && (
              <p className="text-sm text-red-600 mt-1">{errors.display_name.message}</p>
            )}
          </div>

          {/* Slug/Name */}
          <div>
            <Label htmlFor="name">
              Slug <span className="text-red-500">*</span>
            </Label>
            <Input
              id="name"
              placeholder="e.g., structural_engineer"
              className="font-mono text-sm"
              {...register('name')}
              onChange={(e) => {
                setSlugManuallyEdited(true);
                register('name').onChange(e);
              }}
            />
            <p className="text-xs text-gray-500 mt-1">
              {isEditMode
                ? 'Internal identifier (lowercase, underscores)'
                : 'Auto-generated from display name (editable)'}
            </p>
            {errors.name && <p className="text-sm text-red-600 mt-1">{errors.name.message}</p>}
          </div>

          {/* Display Order */}
          <div>
            <Label htmlFor="display_order">
              Display Order <span className="text-red-500">*</span>
            </Label>
            <Input
              id="display_order"
              type="number"
              min={0}
              placeholder="0"
              {...register('display_order', { valueAsNumber: true })}
            />
            <p className="text-xs text-gray-500 mt-1">
              Controls the order in which types appear in dropdowns (lower numbers first)
            </p>
            {errors.display_order && (
              <p className="text-sm text-red-600 mt-1">{errors.display_order.message}</p>
            )}
          </div>

          {/* Is Client Type Checkbox */}
          <div className="flex items-start space-x-3 pt-2">
            <Checkbox
              id="is_client_type"
              checked={isClientType}
              onCheckedChange={(checked) => setValue('is_client_type', checked === true)}
            />
            <div>
              <Label
                htmlFor="is_client_type"
                className="text-sm font-medium leading-none cursor-pointer"
              >
                Mark as Client Type
              </Label>
              <p className="text-sm text-gray-500 mt-1">
                Check this if this type represents the client on projects. Only one
                client type should be marked.
              </p>
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={createScopeType.isPending || updateScopeType.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={createScopeType.isPending || updateScopeType.isPending}
            >
              {createScopeType.isPending || updateScopeType.isPending
                ? 'Saving...'
                : isEditMode
                  ? 'Update'
                  : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
